import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Input,
  IconButton,
  Button,
  Badge,
  Avatar,
  Icon,
  useColorModeValue,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Select,
  useToast,
  Flex,
} from '@chakra-ui/react';
import {
  FiSend,
  FiPaperclip,
  FiMessageSquare,
  FiUsers,
  FiFileText,
  FiShoppingCart,
  FiPackage,
  FiDollarSign,
  FiChevronDown,
  FiCheck,
  FiExternalLink,
} from 'react-icons/fi';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useNavigate } from 'react-router-dom';

interface TeamChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: any;
  currentUserId: string;
  currentUserName: string;
  currentUserRole?: string;
  currentUserDept?: string;
}

export const TeamChatDrawer: React.FC<TeamChatDrawerProps> = ({
  isOpen,
  onClose,
  organizationId,
  currentUserId,
  currentUserName,
  currentUserRole = 'staff',
  currentUserDept = 'procurement',
}) => {
  const [selectedChannelId, setSelectedChannelId] = useState<string>('seed-channel-procurement-sales');
  const [messageText, setMessageText] = useState<string>('');
  
  // Attached document state
  const [attachedDocType, setAttachedDocType] = useState<string>('');
  const [attachedDocRef, setAttachedDocRef] = useState<string>('');
  const [attachedDocTitle, setAttachedDocTitle] = useState<string>('');
  const [isAttachOpen, setIsAttachOpen] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const navigate = useNavigate();

  const bg = useColorModeValue('white', 'gray.850');
  const chatBubbleSelf = useColorModeValue('blue.500', 'blue.600');
  const chatBubbleOther = useColorModeValue('gray.100', 'gray.700');
  const docCardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Query channels & messages (lazy loaded only when drawer is opened)
  const channels = useQuery(api.teamChat.listChannels, isOpen ? {
    organization_id: organizationId,
    user_id: currentUserId,
  } : 'skip');

  const messages = useQuery(api.teamChat.listMessages, isOpen && selectedChannelId ? {
    channel_id: selectedChannelId,
  } : 'skip');

  const sendMessageMutation = useMutation(api.teamChat.sendMessage);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const activeChannel = channels?.find((c: any) => c._id === selectedChannelId) || channels?.[1] || {
    display_name: '🛒 Procurement ⇄ Sales',
    description: 'Fast-track pricing checks and urgent quote sourcing',
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && !attachedDocRef) return;

    try {
      await sendMessageMutation({
        organization_id: organizationId,
        channel_id: selectedChannelId,
        sender_id: currentUserId,
        sender_name: currentUserName,
        sender_role: currentUserDept || currentUserRole,
        message: messageText.trim() || `Shared ERP Document: ${attachedDocRef}`,
        attached_document_type: attachedDocType || undefined,
        attached_document_id: attachedDocRef ? `doc-${Date.now()}` : undefined,
        attached_document_ref: attachedDocRef || undefined,
        attached_document_title: attachedDocTitle || undefined,
      });

      setMessageText('');
      setAttachedDocType('');
      setAttachedDocRef('');
      setAttachedDocTitle('');
      setIsAttachOpen(false);
    } catch (err: any) {
      console.error('Failed to send message:', err);
      toast({
        title: 'Message sending failed',
        description: err.message || 'Please check your connection',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getDocIcon = (type?: string) => {
    switch (type) {
      case 'quotation':
        return FiFileText;
      case 'purchase_order':
        return FiShoppingCart;
      case 'invoice':
        return FiDollarSign;
      case 'material_receipt':
        return FiPackage;
      default:
        return FiFileText;
    }
  };

  const navigateToDoc = (type?: string, ref?: string) => {
    onClose();
    if (type === 'quotation') {
      navigate('/sales/quotations');
    } else if (type === 'purchase_order') {
      navigate('/procurement/orders');
    } else if (type === 'invoice') {
      navigate('/accounting/invoices');
    } else {
      navigate('/procurement');
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent bg={bg}>
        <DrawerCloseButton />
        
        {/* Drawer Header with Channel Selector */}
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} pb={3}>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" pr={6}>
              <HStack spacing={2}>
                <Icon as={FiMessageSquare} color="blue.500" boxSize={5} />
                <Heading size="sm">Cross-Department Chat</Heading>
              </HStack>
              <Badge colorScheme="green" variant="subtle">Live Synchronized</Badge>
            </HStack>

            {/* Channel Switcher */}
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                rightIcon={<Icon as={FiChevronDown} />}
                textAlign="left"
                fontWeight="semibold"
                borderRadius="lg"
              >
                <HStack>
                  <Text isTruncated maxW="280px">
                    {(activeChannel as any).display_name || 'Select Channel'}
                  </Text>
                </HStack>
              </MenuButton>
              <MenuList zIndex={1500} shadow="lg">
                {channels?.map((ch: any) => (
                  <MenuItem
                    key={ch._id}
                    onClick={() => setSelectedChannelId(ch._id)}
                    fontWeight={selectedChannelId === ch._id ? 'bold' : 'normal'}
                  >
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm">{ch.display_name}</Text>
                      <Text fontSize="2xs" color="gray.500">{ch.description}</Text>
                    </VStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            <Text fontSize="xs" color="gray.500">
              {(activeChannel as any).description}
            </Text>
          </VStack>
        </DrawerHeader>

        {/* Message Stream */}
        <DrawerBody p={4} overflowY="auto">
          <VStack spacing={4} align="stretch">
            {messages?.map((msg: any) => {
              const isSelf = msg.sender_id === currentUserId;

              return (
                <Flex
                  key={msg._id}
                  direction="column"
                  align={isSelf ? 'flex-end' : 'flex-start'}
                  w="full"
                >
                  <HStack spacing={1.5} mb={1}>
                    {!isSelf && (
                      <Avatar
                        size="2xs"
                        name={msg.sender_name}
                      />
                    )}
                    <Text fontSize="2xs" fontWeight="bold" color="gray.500">
                      {msg.sender_name} {msg.sender_role && `(${msg.sender_role})`}
                    </Text>
                    <Text fontSize="2xs" color="gray.400">
                      • {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Text>
                  </HStack>

                  <Box
                    maxW="85%"
                    bg={isSelf ? chatBubbleSelf : chatBubbleOther}
                    color={isSelf ? 'white' : 'inherit'}
                    px={3.5}
                    py={2.5}
                    borderRadius="2xl"
                    borderBottomRightRadius={isSelf ? '4px' : '2xl'}
                    borderBottomLeftRadius={!isSelf ? '4px' : '2xl'}
                    boxShadow="sm"
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {msg.message}
                    </Text>

                    {/* Embedded ERP Document Card */}
                    {msg.attached_document_ref && (
                      <Box
                        mt={2.5}
                        p={2.5}
                        bg={docCardBg}
                        color="inherit"
                        borderRadius="lg"
                        border="1px"
                        borderColor={borderColor}
                        shadow="xs"
                        cursor="pointer"
                        _hover={{ borderColor: 'blue.400', shadow: 'sm' }}
                        onClick={() => navigateToDoc(msg.attached_document_type, msg.attached_document_ref)}
                      >
                        <HStack spacing={2} justify="space-between">
                          <HStack spacing={2}>
                            <Icon
                              as={getDocIcon(msg.attached_document_type)}
                              color="blue.500"
                              boxSize={4}
                            />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="xs" fontWeight="bold" color="blue.600">
                                {msg.attached_document_ref}
                              </Text>
                              <Text fontSize="2xs" color="gray.500" isTruncated maxW="180px">
                                {msg.attached_document_title || 'Linked Transaction Document'}
                              </Text>
                            </VStack>
                          </HStack>
                          <Badge size="xs" colorScheme="blue">
                            <Icon as={FiExternalLink} mr={1} /> Open
                          </Badge>
                        </HStack>
                      </Box>
                    )}
                  </Box>
                </Flex>
              );
            })}
            <div ref={messagesEndRef} />
          </VStack>
        </DrawerBody>

        {/* Input & Attach Composer */}
        <DrawerFooter borderTopWidth="1px" borderColor={borderColor} p={3}>
          <VStack align="stretch" w="full" spacing={2}>
            {/* Attached Doc Preview Badge */}
            {attachedDocRef && (
              <HStack
                p={2}
                bg={useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.15)')}
                borderRadius="md"
                justify="space-between"
              >
                <HStack spacing={2}>
                  <Icon as={FiFileText} color="blue.500" />
                  <Text fontSize="xs" fontWeight="bold">
                    Attached: {attachedDocRef} ({attachedDocTitle || attachedDocType})
                  </Text>
                </HStack>
                <IconButton
                  aria-label="Remove attachment"
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    setAttachedDocType('');
                    setAttachedDocRef('');
                    setAttachedDocTitle('');
                  }}
                  icon={<Icon as={FiCheck} />}
                />
              </HStack>
            )}

            {/* Quick 1-Click Transaction Card Embedding Chips */}
            <HStack spacing={2} pb={1} wrap="wrap">
              <Text fontSize="2xs" color="gray.400" fontWeight="bold">Embed Card:</Text>
              <Button
                size="2xs"
                variant="subtle"
                colorScheme="blue"
                borderRadius="full"
                leftIcon={<Icon as={FiFileText} boxSize={3} />}
                onClick={() => {
                  setAttachedDocType('quotation');
                  setAttachedDocRef('QT-2026-');
                  setIsAttachOpen(true);
                }}
              >
                @quote
              </Button>
              <Button
                size="2xs"
                variant="subtle"
                colorScheme="green"
                borderRadius="full"
                leftIcon={<Icon as={FiShoppingCart} boxSize={3} />}
                onClick={() => {
                  setAttachedDocType('purchase_order');
                  setAttachedDocRef('PO-2026-');
                  setIsAttachOpen(true);
                }}
              >
                @po
              </Button>
              <Button
                size="2xs"
                variant="subtle"
                colorScheme="purple"
                borderRadius="full"
                leftIcon={<Icon as={FiDollarSign} boxSize={3} />}
                onClick={() => {
                  setAttachedDocType('invoice');
                  setAttachedDocRef('INV-2026-');
                  setIsAttachOpen(true);
                }}
              >
                @invoice
              </Button>
            </HStack>

            <HStack spacing={2}>
              {/* Document Attacher Popover */}
              <Popover isOpen={isAttachOpen} onClose={() => setIsAttachOpen(false)} placement="top-start">
                <PopoverTrigger>
                  <IconButton
                    aria-label="Attach Document"
                    icon={<Icon as={FiPaperclip} />}
                    size="sm"
                    variant="outline"
                    onClick={() => setIsAttachOpen(!isAttachOpen)}
                    colorScheme={attachedDocRef ? 'blue' : 'gray'}
                  />
                </PopoverTrigger>
                <PopoverContent p={2} zIndex={1600} shadow="xl" w="280px">
                  <PopoverArrow />
                  <PopoverCloseButton size="sm" />
                  <PopoverHeader fontWeight="bold" fontSize="xs">Embed ERP Document</PopoverHeader>
                  <PopoverBody>
                    <VStack spacing={2} align="stretch">
                      <Select
                        size="xs"
                        value={attachedDocType}
                        onChange={(e) => setAttachedDocType(e.target.value)}
                        placeholder="Document Category"
                      >
                        <option value="quotation">Quotation (QT)</option>
                        <option value="purchase_order">Purchase Order (PO)</option>
                        <option value="requisition">Requisition (PR)</option>
                        <option value="invoice">ZATCA Tax Invoice (INV)</option>
                      </Select>

                      <Input
                        size="xs"
                        placeholder="Document Ref (e.g. QT-2026-0091)"
                        value={attachedDocRef}
                        onChange={(e) => setAttachedDocRef(e.target.value)}
                      />

                      <Input
                        size="xs"
                        placeholder="Title / Material brief (e.g. Pipes)"
                        value={attachedDocTitle}
                        onChange={(e) => setAttachedDocTitle(e.target.value)}
                      />

                      <Button
                        size="xs"
                        colorScheme="blue"
                        onClick={() => setIsAttachOpen(false)}
                        isDisabled={!attachedDocRef}
                      >
                        Attach Card
                      </Button>
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Popover>

              <Input
                placeholder={`Message #${(activeChannel as any).name || 'team'}...`}
                size="sm"
                borderRadius="full"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
              />

              <IconButton
                aria-label="Send message"
                icon={<Icon as={FiSend} />}
                size="sm"
                colorScheme="blue"
                borderRadius="full"
                onClick={handleSendMessage}
                isDisabled={!messageText.trim() && !attachedDocRef}
              />
            </HStack>
          </VStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
